import type { AccuLiveDTO, AccuMarketState } from './accumulator';
import type { Direction, DurationUnit, Modality } from './contracts';
import type { ProgressionConfig } from './progression';
import type { RoundKind, RoundReference, RoundResult } from './rounds';

export const SESSION_STATUSES = ['CONFIGURING', 'WAITING', 'ACTIVE', 'TRADING', 'PAUSED', 'ENDED', 'ERROR'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/** Statuses in which a worker must own and run the session. */
export const RUNNABLE_SESSION_STATUSES: readonly SessionStatus[] = ['WAITING', 'ACTIVE', 'TRADING'];

export type ContractStatus = 'OPEN' | 'WON' | 'LOST' | 'SOLD' | 'CANCELLED';
export type OrderStatus = 'PENDING' | 'SENT' | 'FILLED' | 'REJECTED' | 'FAILED' | 'UNKNOWN' | 'CANCELLED';
export type AccountType = 'DEMO' | 'REAL';
export type TradeSource = 'MANUAL' | 'AUTOMATION';

export interface AccountDTO {
  id: string;
  derivAccountId: string;
  type: AccountType;
  currency: string;
  status: string;
  balance: string | null;
  balanceUpdatedAt: string | null;
  isSelected: boolean;
  realTradingEnabled: boolean;
  connection: { authType: 'OAUTH' | 'PAT'; status: string; expiresAt: string | null };
}

export interface QuoteDTO {
  quoteId: string;
  accountId: string;
  modality: Modality;
  direction: Direction;
  symbol: string;
  contractType: string;
  stake: string;
  askPrice: string;
  payout: string;
  potentialProfit: string;
  returnPct: string;
  spot: number;
  longcode: string;
  currency: string;
  duration: number;
  durationUnit: DurationUnit;
  barrier: number | null;
  minStake: string | null;
  maxStake: string | null;
  createdAt: string;
  validUntil: string;
  /** Round this quote prices (entries go into it while betting is open); null for Traderoom entries. */
  roundId: number | null;
}

/** An entry waiting for its round (queued) or just sent to the broker. */
export interface RoundOrderDTO {
  id: string;
  accountId: string;
  source: TradeSource;
  sessionId: string | null;
  roundId: number;
  symbol: string;
  modality: Modality | null;
  direction: Direction | null;
  stake: string;
  expectedPayout: string | null;
  currency: string;
  status: OrderStatus;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  /** Decolagem bet: the round takeoff it is bought for (epoch s), else null. */
  takeoff?: number | null;
}

export interface ContractDTO {
  id: string;
  derivContractId: string;
  accountId: string;
  accountType: AccountType;
  sessionId: string | null;
  source: TradeSource;
  symbol: string;
  modality: Modality | null;
  direction: Direction | null;
  contractType: string;
  barrier: string | null;
  stake: string;
  payout: string;
  sellPrice: string | null;
  profit: string | null;
  status: ContractStatus;
  entrySpot: string | null;
  exitSpot: string | null;
  entryAt: string | null;
  exitAt: string | null;
  currency: string;
  duration: number | null;
  durationUnit: string | null;
  purchasedAt: string;
  settledAt: string | null;
  longcode: string | null;
  roundId: number | null;
  /** Traderoom product (digital, blitz, binary); null on round entries and /operar game entries. */
  product?: string | null;
  /** Acumulador: growth per tick (e.g. "0.03"), the take profit set at the buy and the ticks it lasted. */
  growthRate: string | null;
  takeProfit: string | null;
  tickCount: number | null;
}

export interface SessionDTO {
  id: string;
  configurationId: string;
  name: string;
  status: SessionStatus;
  endReason: string | null;
  lastError: string | null;
  accountId: string;
  accountType: AccountType;
  currency: string;
  symbol: string;
  modality: Modality;
  strategyKey: string;
  baseStake: string;
  currentStake: string;
  currentLevel: number;
  targetProfit: string;
  maxLoss: string;
  maxTrades: number;
  maxConcurrent: number;
  progression: ProgressionConfig;
  trades: number;
  wins: number;
  losses: number;
  netProfit: string;
  volume: string;
  openContracts: number;
  openExposure: string;
  startedAt: string | null;
  endedAt: string | null;
  updatedAt: string;
  /** Last round the strategy read (null before the first one). */
  lastReadAt: string | null;
  /** Rounds read since the last entry (or since start). */
  idleRounds: number;
  /** What a custom strategy is doing now ("Loss virtual 1 de 2", "Pausa…"); null for the built-in one. */
  strategyNote: string | null;
  /** Robots of "Meus robôs" this session runs (one, or two combined); empty for the built-in robots. */
  robotIds: string[];
}

export interface SummaryDTO {
  trades: number;
  settled: number;
  wins: number;
  losses: number;
  winRate: number;
  netProfit: string;
  volume: string;
}

// ---- Realtime ---------------------------------------------------------------

export type RealtimeServerMessage =
  | { type: 'hello'; userId: string }
  /** Answer to a ping: `t` echoes the client's send time, `now` is the server clock (ms). */
  | { type: 'pong'; t?: number; now: number }
  /** `refs`: official openings of the rounds running now (see RoundReference), once known. */
  | { type: 'tick'; symbol: string; epoch: number; quote: number; pipSize: number; digit: number; rounds?: RoundResult[]; refs?: RoundReference[] }
  | { type: 'rounds_snapshot'; symbol: string; kind: RoundKind; rounds: RoundResult[] }
  | { type: 'order'; order: RoundOrderDTO }
  | { type: 'ticks_snapshot'; symbol: string; pipSize: number; ticks: { epoch: number; quote: number; digit: number }[] }
  | { type: 'contract'; contract: ContractDTO }
  | { type: 'session'; session: SessionDTO }
  | { type: 'balance'; accountId: string; balance: string; currency: string; at: string }
  /** A deposit was moved from the Deriv Wallet to the Options account (WalletService). */
  | { type: 'wallet_transfer'; amount: string; currency: string; at: string }
  | { type: 'notification'; level: 'info' | 'success' | 'warning' | 'error'; title: string; body?: string }
  /** Acumulador market, one message per growth rate per tick. */
  | { type: 'accu'; state: AccuMarketState }
  | { type: 'accu_snapshot'; symbol: string; states: AccuMarketState[] }
  /** The user's open Acumulador contract, every tick. */
  | { type: 'accu_live'; live: AccuLiveDTO }
  | { type: 'error'; message: string };

export type RealtimeClientMessage =
  | { type: 'subscribe_ticks'; symbol: string }
  | { type: 'unsubscribe_ticks'; symbol: string }
  | { type: 'subscribe_accu'; symbol: string }
  | { type: 'unsubscribe_accu'; symbol: string }
  | { type: 'ping'; t?: number }
  /** This device's app is on screen (or not): results it already shows skip its push notifications. */
  | { type: 'presence'; deviceId: string; visible: boolean };

/** Automatic Wallet → Options transfer of new Deriv deposits, as the user sees it. */
export interface WalletStatusDTO {
  /**
   * active: deposits move on their own. reconnect: the connection predates the permission (one
   * reconnect turns it on). off: switched off by the platform. unavailable: no real account / not connected.
   */
  autoTransfer: 'active' | 'reconnect' | 'off' | 'unavailable';
  /** Being moved now (sent, not confirmed yet). */
  moving: string | null;
  lastTransfer: { amount: string; currency: string; at: string } | null;
}
